import React from "react";
import GuarantorPreview from "../forms/GuarantorPreview";
import { useSelector, useDispatch } from "react-redux";
import {
	acknowledgeLoanRequest,
	rejectLoanRequest,
	fetchLoanRequest,
	searchLoanRequests,
} from "../../store/actions/adminActions";

import { clearNotifications } from "../../store/actions/notificationsActions";

import Swal from "sweetalert2";
import LoanTable from "./LoanTable";
import MyPagination from "../utilities/Pagination";
import Loader from "../utilities/Loader";
import ContentWrapper from "../utilities/ContentWrapper";

export default function LoanRequest() {
	const dispatch = useDispatch();

	const [currPage, setCurrPage] = React.useState(1);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [isSearching, setIsSearching] = React.useState(false);
	const [searchTimeout, setSearchTimeout] = React.useState(null);

	const { loanRequests: requests, isLoading, loanRequestPageNum: numberOfPages } = useSelector((state) => state.admin);

	const notification = useSelector((state) => state.notification);

	React.useEffect(() => {
		const successMessage = notification?.success?.message;
		const errorMessage = notification?.errors?.message;

		if (successMessage) {
			Swal.fire({
				icon: "success",
				text: successMessage,
				showConfirmButton: false,
				timer: 2000,
			});
		} else if (errorMessage) {
			Swal.fire({
				icon: "error",
				text: errorMessage,
				showConfirmButton: false,
				timer: 2000,
			});
		}
		dispatch(clearNotifications());
	}, [notification, dispatch]);

	React.useEffect(() => {
		if (!isSearching) {
			dispatch(fetchLoanRequest(currPage || 1));
		}
	}, [dispatch, currPage, isSearching]);

	const [showGuarantor, setShowGuarantor] = React.useState();

	const handleDelete = (id) => {
		Swal.fire({
			title: "Are you sure you want to delete this user?",
			text: "This action cannot be undone.",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, delete",
			cancelButtonText: "Cancel",
		}).then((result) => {
			if (result.isConfirmed) {
				dispatch(rejectLoanRequest(id));
			} else {
				Swal.fire("Cancelled", "The action was cancelled.", "info");
			}
		});
	};

	const handleConfirm = (trx) => {
		const guarantors = trx.guarantors;

		const message =
			guarantors.length > 0
				? "This action cannot be undone."
				: "This loan request is missing the required number of guarantors";
		Swal.fire({
			title: "Are you sure you want to confirm this loan?",
			text: message,
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, confirm",
			cancelButtonText: "Cancel",
		}).then((result) => {
			if (result.isConfirmed) {
				dispatch(acknowledgeLoanRequest({ transactionId: trx.transactionId }));
			} else {
				Swal.fire("Cancelled", "The action was cancelled.", "info");
			}
		});
	};

	const showLoanPage = () => {
		setShowGuarantor(null);
	};

	const afterPageClicked = (page_num) => {
		setCurrPage(page_num);
		if (isSearching) {
			dispatch(searchLoanRequests(searchTerm, page_num));
		} else {
			dispatch(fetchLoanRequest(page_num));
		}
	};

	const performSearch = (term, page = 1) => {
		if (term.trim()) {
			setIsSearching(true);
			setCurrPage(page);
			dispatch(searchLoanRequests(term, page));
		} else {
			handleClearSearch();
		}
	};

	const handleSearchInputChange = (e) => {
		const value = e.target.value;
		setSearchTerm(value);

		// Clear previous timeout
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// Set new timeout for debounced search
		const timeout = setTimeout(() => {
			performSearch(value, 1);
		}, 500); // Wait 500ms after user stops typing

		setSearchTimeout(timeout);
	};

	const handleSearchSubmit = (e) => {
		e.preventDefault();
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		performSearch(searchTerm, 1);
	};

	const handleClearSearch = () => {
		setSearchTerm("");
		setIsSearching(false);
		setCurrPage(1);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		dispatch(fetchLoanRequest(1));
	};

	if (isLoading) return <Loader isLoading={isLoading} />

	return (
		<ContentWrapper>
			{showGuarantor ? (
				<GuarantorPreview data={showGuarantor} showLoanPage={showLoanPage} />
			) : (
				<>
					{/* Search Bar */}
					<div className="card mb-3">
						<div className="card-body">
							<form onSubmit={handleSearchSubmit} className="row g-3">
								<div className="col-md-8">
									<div className="input-group">
										<input
											type="text"
											className="form-control"
											placeholder="Search by loanee name, membership ID, or transaction ID..."
											value={searchTerm}
											onChange={handleSearchInputChange}
										/>
										<button
											className="btn btn-outline-primary"
											type="submit"
											disabled={!searchTerm.trim()}
										>
											<i className="fas fa-search"></i> Search
										</button>
									</div>
								</div>
								{isSearching && (
									<div className="col-md-4">
										<button
											type="button"
											className="btn btn-outline-secondary"
											onClick={handleClearSearch}
										>
											<i className="fas fa-times"></i> Clear Search
										</button>
									</div>
								)}
							</form>
							{isSearching && (
								<small className="text-muted mt-2 d-block">
									<i className="fas fa-search text-primary"></i> Showing search results for: <strong>"{searchTerm}"</strong>
									{requests.length > 0 && (
										<span className="ms-2">
											({requests.length} result{requests.length !== 1 ? 's' : ''} found)
										</span>
									)}
								</small>
							)}
						</div>
					</div>

					<MyPagination
						currentPage={currPage}
						numberOfPages={numberOfPages}
						pageClicked={(ele) => {
							afterPageClicked(ele);
						}}
					>
						<LoanTable
							loanRequests={requests}
							setShowGuarantor={setShowGuarantor}
							handleConfirm={handleConfirm}
							handleDelete={handleDelete}
							isSearching={isSearching}
							searchTerm={searchTerm}
						/>
					</MyPagination>
				</>
			)}
		</ContentWrapper>
	);
}